import {useEffect, useRef, useState} from 'react';
import type {Location} from 'react-router-dom';
import {useLocation, useNavigate} from 'react-router-dom';

import {appPaths} from '@/config/appPaths';
import {VITE_ADMIN_SSO_ENABLED} from '@/config/endpoints';
import {useAuth} from '@/hooks/useAuth';
import {authService} from '@/services/authService';
import type {AfriexRoles} from '@/types/AfriexRoles';
import {hasAdminRole, parseJwt, saveAdminSession} from '@/utils/auth';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';

/**
 * In production the admin is behind Cloudflare Access, so the person on the
 * login page has already signed in with Google and Access MFA. This trades
 * that identity for the admin token, so there is no second login. If it
 * fails, the email, password and OTP form is shown instead. Off outside
 * production (`VITE_ADMIN_SSO_ENABLED`).
 */
const useSsoLogin = () => {
  const navigate = useNavigate();
  const {state} = useLocation() as {state: {from?: Location} | null};
  const {login} = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(VITE_ADMIN_SSO_ENABLED);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!VITE_ADMIN_SSO_ENABLED || hasStarted.current) {
      return;
    }
    hasStarted.current = true;

    const signIn = async () => {
      try {
        const tokenResponse = await authService.sso();
        const token = tokenResponse?.tokens?.admin;
        if (!hasAdminRole(token)) {
          throw new Error('Unauthorized');
        }
        // hasAdminRole above confirmed the token carries a non-'user' role.
        const adminRole = parseJwt(token)?.role as AfriexRoles;
        login(adminRole, saveAdminSession(tokenResponse, adminRole));
        await navigate(state?.from?.pathname || `/${appPaths.users}`, {
          replace: true,
        });
      } catch (error) {
        setIsSigningIn(false);
        showErrorMessage(
          getErrorMessage(
            error,
            'Single sign-on failed. Sign in with your email instead.',
          ),
        );
      }
    };
    // signIn handles its own failure; this only fires if a setter throws.
    signIn().catch(() => setIsSigningIn(false));
  }, [login, navigate, state]);

  return {isSigningIn};
};

export default useSsoLogin;
